/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import FullPageLayout from "../../../templates/FullPageLayout";
import { Button, Card, Col, Form, Row } from "antd";
import { DateField, InputField, SelectField } from "../../../atoms/FormFields";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { getProposalBuilderData } from "../ducks/actions";
import { sentProposalDoc } from "../ducks/services";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { BASE_URLS, ENVIRONMENT_MODE } from "../../../config/constants";
import { CrossIcon } from "../../../atoms/CustomIcons";

interface FormValues {
  send_to?: string[];
  subject: string;
  proposal_end_date: string;
}

const emailUrl = BASE_URLS[ENVIRONMENT_MODE].REACT_APP_EMAIL_URL;

export default function SendProposalByEmail() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [sentOptions, setSentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [emailText, setEmailText] = useState<string>("");
  const locationUrl = window.location.pathname;
  const pathSegments = locationUrl.split("/");
  // const companyId = pathSegments[pathSegments.length - 1];
  // const dealId = pathSegments[pathSegments.length - 2];
  const proposalId = pathSegments[pathSegments.length - 1];
  const proposalBuilderData = useAppSelector(
    (state) => state?.potentialDeal?.proposalBuilderData
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = (val: FormValues) => {
    setLoading(true);
    console.log("Form submitted with values:", val);

    let clientContacts: any[] = [];
    let clientContactsEmail: any[] = [];
    // Support tags: separate selected contact IDs from ad-hoc email addresses
    const toEmail = (e: any) =>
      String(e ?? "")
        .trim()
        .toLowerCase();
    const rawValues = Array.isArray(val?.send_to) ? val.send_to : [];

    const contacts =
      proposalBuilderData?.data?.proposal?.potential_deal?.property?.client
        ?.clientContacts ?? [];

    const knownIds = new Set(
      contacts
        .map((c: any) => c?.id)
        .filter((x: any) => typeof x === "string" && x)
    );

    const isEmail = (s: string) =>
      typeof s === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(s.trim());

    const selectedIds: string[] = [];
    const customEmailsSet = new Set<string>();

    for (const v of rawValues) {
      const value = (v ?? "").toString().trim();
      if (!value) continue;
      if (knownIds.has(value)) {
        selectedIds.push(value);
      } else if (isEmail(value)) {
        customEmailsSet.add(value.toLowerCase());
      }
    }

    // Map selected IDs to contact objects and emails
    clientContacts = contacts.filter((c: any) =>
      selectedIds.includes(c?.id ?? "")
    );

    clientContactsEmail = [
      ...new Set([
        ...clientContacts.map((c: any) => toEmail(c?.email)).filter(Boolean),
        ...Array.from(customEmailsSet),
      ]),
    ];

    // Prevent the block below from re-computing and overwriting the above
    // by skipping its `if (proposalBuilderData?.data && val?.send_to)` branch.
    val.send_to = undefined;
    if (proposalBuilderData?.data && val?.send_to) {
      clientContacts =
        proposalBuilderData.data?.proposal?.potential_deal?.property?.client?.clientContacts?.filter(
          (contact) => val?.send_to?.includes(contact?.id ?? "")
        );
      clientContactsEmail =
        proposalBuilderData.data?.proposal?.potential_deal?.property?.client?.clientContacts
          ?.filter((contact) => val?.send_to?.includes(contact?.id ?? ""))
          ?.map((contact) => contact?.email);
    }

    clientContactsEmail = [
      ...(clientContactsEmail ?? []),
      proposalBuilderData?.data?.responsible_manager?.email,
    ];

    const payload = {
      proposal_id: proposalId,
      company_id: proposalBuilderData?.data?.company?.id ?? "",
      emails: clientContactsEmail ?? [],
      subject: val.subject,
      client_contact: clientContacts?.[0],
      html: emailText,
      expiry_date: val?.proposal_end_date
        ? dayjs(val.proposal_end_date).format("YYYY-MM-DD")
        : undefined,
      status: "sent",
    };
    sentProposalDoc(payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Proposal sent successfully.");
        navigate(`/potential-deals/${proposalBuilderData?.data?.company?.id}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (proposalId) {
      const payload = {
        proposal_id: proposalId,
      };
      dispatch(getProposalBuilderData(payload));
    }
  }, [dispatch, proposalId]);

  useEffect(() => {
    if (proposalBuilderData?.data) {
      const dummyDropDown: { label: string; value: string }[] = [];
      const signIds: string[] = [];
      proposalBuilderData.data?.proposal?.potential_deal?.property?.client?.clientContacts?.forEach(
        (contact) => {
          if (contact?.is_signing) {
            signIds.push(contact?.id ?? "");
          }

          dummyDropDown.push({
            label: `${contact?.first_name ?? ""} ${contact?.last_name ?? ""}`,
            value: contact?.id,
          });
        }
      );
      setValue("send_to", signIds ?? []);
      setSentOptions(dummyDropDown);
    }
  }, [proposalBuilderData?.data, setValue]);

  useEffect(() => {
    setValue("subject", "Landscaping services from Southern Landscape Group");
  }, [setValue]);

  useEffect(() => {
    if (proposalId) {
      setEmailText(`
    <p>Hi ${
      proposalBuilderData?.data?.proposal?.potential_deal?.property?.client
        ?.name ?? ""
    },</p><br/>
    <p>
      Please click on the 
      <a href="${emailUrl}/proposal-builder/${proposalId}" target="_blank">link</a> 
      for your review. If you have any questions or need further information, feel free to reach out.
    </p><br/>
    <p>
      We look forward to your feedback and hope to move forward with this opportunity.
    </p><br/>
    <p>Thanks</p>
  `);
    }
  }, [proposalId, proposalBuilderData?.data]);

  return (
    <FullPageLayout loading={proposalBuilderData?.loader || loading}>
      <Row gutter={8} style={{ width: "672px", paddingBottom: 60 }}>
        <Col span={24}>
          <div
            className="title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h2 style={{ marginBottom: "4px" }}> Send proposal by email </h2>
            </div>
          </div>
        </Col>

        <Form
          layout="vertical"
          onFinish={handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
          style={{ width: "100%" }}
        >
          <Col span={24}>
            <Card>
              <Row gutter={20}>
                <Col span={24} className="show_cross_icon">
                  <SelectField
                    fieldName="send_to"
                    label="To"
                    control={control}
                    selectOption={sentOptions}
                    iProps={{
                      placeholder: "Select recipient",
                      mode: "tags",
                      // mode: "multiple",
                      removeIcon: <CrossIcon />,
                    }}
                    isRequired
                    rules={{
                      required: "Send to is required.",
                    }}
                    validate={errors.send_to && "error"}
                    validMessage={errors.send_to && errors.send_to.message}
                    hintMessage={
                      proposalBuilderData?.data?.responsible_manager?.email
                        ? `A copy of this email will also be sent to ${proposalBuilderData?.data?.responsible_manager?.email}.`
                        : undefined
                    }
                  />
                </Col>
                <Col span={24}>
                  <InputField
                    fieldName="subject"
                    label="Subject"
                    control={control}
                    defaultValue=""
                    iProps={{
                      placeholder: "Enter email subject",
                    }}
                    isRequired
                    rules={{
                      required: "Subject is required.",
                    }}
                    validate={errors.subject && "error"}
                    validMessage={errors.subject && errors.subject.message}
                  />
                </Col>

                <Col span={24}>
                  <label
                    style={{
                      marginBottom: "4px",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "20px",
                      color: "#151718",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Email Text
                  </label>
                  <ReactQuill
                    placeholder="Write email here ..."
                    style={{ paddingBottom: "5px" }}
                    className="emailText"
                    value={emailText}
                    onChange={setEmailText}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        ["link", "image"],
                        ["clean"],
                      ],
                    }}
                    formats={[
                      "header",
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "list",
                      "bullet",
                      "align",
                      "link",
                      "image",
                    ]}
                  />
                </Col>

                <Col
                  span={24}
                  style={{
                    display: "flex",
                    gap: "16px",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <DateField
                      fieldName="proposal_end_date"
                      label="Proposal expires:"
                      control={control}
                      // isRequired
                      // rules={{
                      //   required: "Proposal expires date is required.",
                      // }}
                      // validate={errors.proposal_end_date && "error"}
                      // validMessage={
                      //   errors.proposal_end_date &&
                      //   errors.proposal_end_date.message
                      // }
                      iProps={{
                        disabledDate: (current: any) => {
                          return (
                            current.startOf("day").valueOf() <
                            dayjs().startOf("day").valueOf()
                          );
                        },
                      }}
                    />
                  </div>
                  <div style={{ width: "100%" }}>
                    <p
                      style={{
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: "#6C7478",
                        fontWeight: 400,
                      }}
                    >
                      Once the proposal expires, the customer won’t be able to
                      view or sign it. You can update the expiration date later.
                    </p>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <div className="fixed_btm_card">
            <Card>
              <div className="centered">
                <Button
                  htmlType="button"
                  className="outlined-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <div style={{ display: "flex", gap: "16px" }}>
                  <Button htmlType="submit" type="primary" className="mint-btn">
                    Send proposal
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </Form>
      </Row>
    </FullPageLayout>
  );
}
